// T045 — Toolbar above the wallets table: search (debounced), balance filter,
// sort dropdown. Each control is fully controlled by the parent and emits a
// query patch. Search placeholder calls out the v1 emaid-only constraint.
import { useEffect, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { SelectButton } from "primereact/selectbutton";

import type {
  WalletListBalanceFilter,
  WalletListSort,
} from "../../../types/wallet";

interface WalletListToolbarProps {
  searchInput: string;
  onSearchInputChange: (next: string) => void;
  balance: WalletListBalanceFilter;
  onBalanceChange: (next: WalletListBalanceFilter) => void;
  sort: WalletListSort;
  onSortChange: (next: WalletListSort) => void;
}

const balanceOptions: { label: string; value: WalletListBalanceFilter }[] = [
  { label: "Any balance", value: "any" },
  { label: "Non-zero", value: "non_zero" },
  { label: "Zero only", value: "zero" },
];

const sortOptions: { label: string; value: WalletListSort }[] = [
  { label: "Last activity (newest first)", value: "last_activity_desc" },
  { label: "Last activity (oldest first)", value: "last_activity_asc" },
  { label: "Spendable balance (high to low)", value: "spendable_balance_desc" },
  { label: "Spendable balance (low to high)", value: "spendable_balance_asc" },
  { label: "Created (newest first)", value: "created_at_desc" },
  { label: "Created (oldest first)", value: "created_at_asc" },
];

const WalletListToolbar = ({
  searchInput,
  onSearchInputChange,
  balance,
  onBalanceChange,
  sort,
  onSortChange,
}: WalletListToolbarProps) => {
  // Local input mirror so typing feels instant; debouncing happens in the parent.
  const [localValue, setLocalValue] = useState(searchInput);
  useEffect(() => {
    setLocalValue(searchInput);
  }, [searchInput]);

  return (
    <div className="flex flex-column lg:flex-row gap-3 lg:align-items-center lg:justify-content-between mb-3">
      <span className="p-input-icon-left w-full lg:w-25rem">
        <i className="pi pi-search ml-3" />
        <InputText
          value={localValue}
          onChange={(event) => {
            const next = event.target.value;
            setLocalValue(next);
            onSearchInputChange(next);
          }}
          placeholder="Search by emaid"
          className="pl-6 w-full"
        />
      </span>

      <div className="flex flex-column sm:flex-row gap-2">
        <SelectButton
          value={balance}
          options={balanceOptions}
          onChange={(event) => {
            if (event.value) onBalanceChange(event.value as WalletListBalanceFilter);
          }}
          allowEmpty={false}
          className="segmented-select"
          pt={{ button: { className: "text-sm" } }}
        />
        <Dropdown
          value={sort}
          options={sortOptions}
          onChange={(event) => onSortChange(event.value as WalletListSort)}
          className="w-full sm:w-20rem"
        />
      </div>
    </div>
  );
};

export default WalletListToolbar;
